// Google Tag Manager — mesma regra de restrição do Meta Pixel (ver metaPixel.ts):
// nunca carrega incondicionalmente no index.html, só nas rotas PÚBLICAS de
// marketing, e só em host de produção. Pedido do dono 15/09/2026 ("por na
// land") — plugado no mesmo isPixelAllowedOnPath()==="public" já usado pro
// Pixel, em vez de colar a tag crua no HTML (que carregaria também em
// /paciente, /medico, /admin, consulta, prontuário — áreas bloqueadas por
// regra documentada no CLAUDE.md).

const GTM_ID = "GTM-T3Q97NFP";

function isProductionHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "blinkmed.com.br" || host === "www.blinkmed.com.br";
}

let gtmLoaded = false;

export function ensureGtmForPublicPage() {
  if (!isProductionHost() || gtmLoaded) return;
  gtmLoaded = true;

  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.insertBefore(script, document.head.firstChild);

  // Equivalente ao <noscript><iframe>: injetado via JS porque o carregamento
  // já é condicional (SPA sem HTML por rota) — mesmo efeito de fallback do
  // snippet oficial para navegadores sem JS não se aplica aqui (a página
  // inteira depende de JS pra renderizar), mas mantido por padrão do GTM.
  if (!document.getElementById("gtm-noscript-iframe")) {
    const noscript = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.id = "gtm-noscript-iframe";
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }
}

// Exceção pontual de privacidade (18/09/2026): /paciente é bloqueada pro GTM
// por regra geral (área com dado clínico depois do login), mas o "sign_up"
// precisa disparar no instante exato do cadastro, que acontece dentro dessa
// mesma rota (SPA, não navega pra URL nova). Chamada só explicitamente do
// próprio handleRegisterSuccess — nunca pelo roteamento automático
// (isPixelAllowedOnPath continua bloqueando /paciente pra tudo mais).
// Reusa a mesma flag gtmLoaded: se o usuário já veio de uma rota pública
// antes, não injeta o container de novo.
export function ensureGtmForRegistration() {
  ensureGtmForPublicPage();
}

// Eventos simples de funil (sign_up / begin_checkout), pedido do time de
// marketing 18/09/2026 (manual próprio): mapear onde os usuários abandonam o
// processo. Sem payload estruturado — só o nome do evento, como especificado.
export function pushGtmSignUp() {
  if (!isProductionHost()) return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event: "sign_up" });
}

export function pushGtmBeginCheckout() {
  if (!isProductionHost()) return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event: "begin_checkout" });
}

// ── Funil de captação de MÉDICO (24/09/2026) ────────────────────────────────
// Pedido da agência: "GTM nas páginas /medico" + 2 conversões (fim do cadastro
// inicial e fim do credenciamento, quando envia os documentos).
//
// Instalar em TODO o /medico foi recusado pelo dono, pelo mesmo motivo que
// existe a restrição por rota deste arquivo: de /medico se chega a
// /consulta/:id e /prontuario/:caseId, cujas URLs carregam id de consulta e de
// paciente — um PageView padrão mandaria isso a terceiros (LGPD). E o GTM é um
// container: quem tem o painel adiciona tags depois, sem code review.
//
// Decisão: carregar SÓ nas duas telas do funil, que são seguras (o médico é o
// lead da campanha e ali não existe dado de paciente). Mesmo padrão da
// "exceção pontual" de /paciente logo acima.
// Área do médico (pedido da agência, 24/09/2026: "a tag do GTM nas páginas com
// /medico"). Cobre TODO o /medico, incluindo painel, agenda, perfil e
// financeiro — não só o funil de cadastro/credenciamento.
//
// ÚNICA proteção mantida (e o motivo dela): `/medico/prontuario/:patientId` é
// uma rota REAL dentro de /medico, e a URL carrega o id do paciente. Por isso
// TUDO que sai daqui passa por `safeTrackingUrl()`, que troca id numérico/uuid
// por ":id" e descarta a query string. A agência enxerga a página
// (`/medico/prontuario/:id`) sem receber a identificação do paciente — atende o
// pedido sem mandar dado clínico a terceiros.
//
// As rotas de consulta em si (`/consulta/:id`, `/prontuario/:caseId`,
// `/chat/:roomId`, `/sala-espera/:id`) NÃO começam com /medico e seguem fora do
// escopo, como antes.
const DOCTOR_AREA_PREFIX = "/medico";

// Rotas da área do médico que carregam DADO CLÍNICO/identificável no path e por
// isso nunca recebem o container (11/10/2026). O `safeTrackingUrl` mascara só o
// que NÓS enviamos ao dataLayer; uma tag configurada no painel do GTM lê
// `location.href` por conta própria e escapa do mascaramento. Medido em
// produção: em `/medico/prontuario/1214` o dataLayer recebia o path mascarado
// (`/medico/prontuario/:id`), mas a tag GA4 da agência enviava
// `dl=https://blinkmed.com.br/medico/prontuario/1214` — id de paciente saindo
// para terceiros. Não dá para corrigir isso por mascaramento; só não carregando.
const DOCTOR_AREA_GTM_DENY = ["/medico/prontuario"];

export function isGtmAllowedOnDoctorArea(pathname: string): boolean {
  const inDoctorArea =
    pathname === DOCTOR_AREA_PREFIX || pathname.startsWith(DOCTOR_AREA_PREFIX + "/");
  if (!inDoctorArea) return false;
  return !DOCTOR_AREA_GTM_DENY.some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  );
}

// Mantido por compatibilidade: as 2 conversões do funil continuam validando a
// rota antes de disparar.
export function isGtmAllowedOnDoctorFunnel(pathname: string): boolean {
  return isGtmAllowedOnDoctorArea(pathname);
}

/**
 * Silencia/religa o rastreamento conforme a rota (11/10/2026).
 *
 * Não basta deixar de CARREGAR o container nas rotas negadas: isto é uma SPA e
 * o médico chega ao prontuário navegando a partir de /medico, quando o GTM já
 * está na página — e container carregado não se descarrega.
 *
 * Medido em produção, nesta ordem:
 *  1. `safeTrackingUrl` mascara o que NÓS mandamos ao dataLayer (o
 *     `page_path` chega correto, `/medico/prontuario/:id`) — mas a tag GA4 do
 *     container lê `location.href` sozinha e manda `dl=` com o id real.
 *  2. `window['ga-disable-<ID>'] = true` NÃO resolve: o GTM envia pela
 *     implementação de GA4 do próprio container, não pelo gtag.js global que
 *     respeita esse interruptor. O hit continuou saindo 2,7s após a navegação,
 *     já com o flag ativo.
 *
 * Então o bloqueio é feito no último ponto que não depende da boa vontade da
 * tag: a própria saída de rede. Enquanto a rota for negada, qualquer hit para
 * um coletor de analytics é descartado (`sendBeacon`, `fetch` e
 * `XMLHttpRequest` — os três caminhos que o GA4 usa, escolhendo um conforme o
 * navegador). Nada é bloqueado nas rotas permitidas, então o funil que a
 * agência pediu continua medindo normalmente.
 */
const COLETORES_ANALYTICS =
  /(^|\.)google-analytics\.com|(^|\.)analytics\.google\.com|(^|\.)stats\.g\.doubleclick\.net|(^|\.)ad\.doubleclick\.net|facebook\.com\/tr/;

let bloqueioInstalado = false;
let rastreamentoBloqueado = false;

function urlDeColetor(alvo: unknown): boolean {
  try {
    const bruto =
      typeof alvo === "string" ? alvo : alvo instanceof URL ? alvo.href : (alvo as Request)?.url;
    if (!bruto) return false;
    return COLETORES_ANALYTICS.test(new URL(bruto, window.location.href).hostname + new URL(bruto, window.location.href).pathname);
  } catch {
    return false;
  }
}

function instalarBloqueioDeRede() {
  if (bloqueioInstalado) return;
  bloqueioInstalado = true;

  const beaconOriginal = navigator.sendBeacon?.bind(navigator);
  if (beaconOriginal) {
    navigator.sendBeacon = function (url: string | URL, data?: BodyInit | null) {
      if (rastreamentoBloqueado && urlDeColetor(url)) return true; // finge sucesso: a tag não tenta de novo
      return beaconOriginal(url as string, data);
    };
  }

  const fetchOriginal = window.fetch?.bind(window);
  if (fetchOriginal) {
    window.fetch = function (entrada: RequestInfo | URL, init?: RequestInit) {
      if (rastreamentoBloqueado && urlDeColetor(entrada)) {
        return Promise.resolve(new Response("", { status: 204 }));
      }
      return fetchOriginal(entrada, init);
    };
  }

  const abrirOriginal = XMLHttpRequest.prototype.open;
  // `open` tem sobrecargas (2 e 5 argumentos); encapsulamos de forma variádica
  // e repassamos tudo como veio, sem assumir a aridade.
  type AberturaXhr = (this: XMLHttpRequest, ...args: unknown[]) => void;
  (XMLHttpRequest.prototype as unknown as { open: AberturaXhr }).open = function (
    this: XMLHttpRequest & { __blinkBloquear?: boolean },
    ...args: unknown[]
  ) {
    this.__blinkBloquear = rastreamentoBloqueado && urlDeColetor(args[1]);
    return (abrirOriginal as unknown as AberturaXhr).apply(this, args);
  };
  const enviarOriginal = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    if ((this as XMLHttpRequest & { __blinkBloquear?: boolean }).__blinkBloquear) return;
    return enviarOriginal.call(this, body);
  };
}

export function setGtmTrackingAllowed(allowed: boolean) {
  try {
    instalarBloqueioDeRede();
    rastreamentoBloqueado = !allowed;

    // Defesa em profundidade: o interruptor oficial do gtag.js. Sozinho não
    // basta (ver comentário acima), mas cobre tags que o respeitem.
    const w = window as unknown as Record<string, unknown>;
    const ids = new Set<string>();
    document
      .querySelectorAll<HTMLScriptElement>('script[src*="googletagmanager.com/gtag/js"]')
      .forEach((s) => {
        const m = s.src.match(/[?&]id=([^&]+)/);
        if (m) ids.add(decodeURIComponent(m[1]));
      });
    for (const id of ids) w[`ga-disable-${id}`] = !allowed;
    w.__blinkTrackingBlocked = !allowed;
  } catch {
    /* rastreamento nunca pode derrubar a navegação */
  }
}

/**
 * URL segura para rastreamento: mascara id numérico/uuid do path e descarta a
 * query string (pode conter resume_token do cadastro). Defesa em profundidade —
 * vale mesmo nas telas do funil, porque qualquer tag configurada no painel do
 * GTM lê `location.href` por conta própria.
 */
export function safeTrackingUrl(href?: string): string {
  try {
    const u = new URL(href || window.location.href);
    const masked = u.pathname
      .split("/")
      .map((seg) =>
        /^\d+$/.test(seg) ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)
          ? ":id"
          : seg
      )
      .join("/");
    return `${u.origin}${masked}`;
  } catch {
    return "";
  }
}

/** Carrega o container em qualquer página da área do médico. */
export function ensureGtmForDoctorArea(pathname: string) {
  if (!isGtmAllowedOnDoctorArea(pathname)) return;
  ensureGtmForPublicPage();
}

/** Alias mantido para as chamadas existentes nas telas do funil. */
export function ensureGtmForDoctorFunnel(pathname: string) {
  ensureGtmForDoctorArea(pathname);
}

/**
 * PageView da área do médico, com a URL mascarada — é isto que permite atender
 * o pedido sem vazar id de paciente de `/medico/prontuario/:patientId`.
 */
export function pushGtmDoctorPageView(pathname: string) {
  if (!isProductionHost() || !isGtmAllowedOnDoctorArea(pathname)) return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event: "page_view", page_path: safeTrackingUrl() });
}

/**
 * As DUAS conversões combinadas com a agência. Allowlist explícito de eventos:
 * evita que alguém passe a mandar evento de consulta/pagamento por aqui sem
 * revisão. Nunca leva dado pessoal (sem nome, e-mail, CPF ou CRM).
 */
export type DoctorFunnelEvent =
  | "medico_cadastro_concluido"
  | "medico_credenciamento_enviado"
  // Dispara quando o médico TOMA CIÊNCIA da aprovação (abre
  // /medico/credenciamento/obrigado), não no instante em que o admin
  // aprovou — a aprovação acontece noutra sessão. Ver a página para detalhe.
  | "medico_credenciamento_aprovado";

export function pushGtmDoctorFunnel(event: DoctorFunnelEvent) {
  if (!isProductionHost()) return;
  if (!isGtmAllowedOnDoctorFunnel(window.location.pathname)) return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event, page_path: safeTrackingUrl() });
}

// Evento "purchase" no padrão GA4 Enhanced Ecommerce, pedido do dono
// 15/09/2026 (via GTM debug: página /obrigado não enviava dados da compra ao
// dataLayer). Espelha os mesmos dados já usados no fbqPurchase (Meta) —
// transaction_id = order id real, value = valor pago, currency fixo BRL.
// Chamar só DEPOIS de ensureGtmForPublicPage() já ter rodado (a página
// /obrigado chama os dois juntos, mesmo padrão do Pixel).
export function pushGtmPurchase(params: { transactionId: string; value: number; itemName?: string; itemCategory?: string }) {
  if (!isProductionHost()) return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ ecommerce: null }); // limpa objeto anterior (recomendação oficial GA4/GTM)
  (window as any).dataLayer.push({
    event: "purchase",
    ecommerce: {
      transaction_id: params.transactionId,
      value: params.value,
      currency: "BRL",
      items: [
        {
          item_name: params.itemName || "consulta",
          item_category: params.itemCategory || "telemedicina",
          price: params.value,
          quantity: 1,
        },
      ],
    },
  });
}
